<script setup lang="ts">
import type { BlockList, Section } from '@/models'
import { type PropType, toRefs, ref } from 'vue'
import CarouselList from './blockListTypes/CarouselList.vue'
import CardsList from './blockListTypes/CardsList.vue'
import GridList from './blockListTypes/GridList.vue'
import FlowList from './blockListTypes/FlowList.vue'

const props = defineProps({
  section: {
    type: Object as PropType<Section>,
    required: true,
  },
  isHeaderBlock: {
    type: Boolean,
    default: false,
  },
})

const propRefs = toRefs(props)
const blockList = ref(propRefs.section.value as BlockList)
</script>

<template>
  <div :id="blockList.blockId">
    <!-- Route to appropriate list type component based on style -->
    <CarouselList
      v-if="blockList.style === 'carousel'"
      :blockList="blockList"
      :isHeaderBlock="isHeaderBlock"
    />
    <CardsList
      v-else-if="blockList.style === 'cards'"
      :blockList="blockList"
      :isHeaderBlock="isHeaderBlock"
      class="max-w-[1512px] m-auto"
    />
    <GridList
      v-else-if="blockList.style === 'grid'"
      :blockList="blockList"
      :isHeaderBlock="isHeaderBlock"
      class="max-w-[1512px] m-auto"
    />
    <FlowList
      v-else-if="blockList.style === 'flow'"
      :blockList="blockList"
      :isHeaderBlock="isHeaderBlock"
    />
  </div>
</template>

<style>
#casestudies .block-text {
  max-width: 1512px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding-left: 3rem;
  padding-right: 3rem;
  padding-top: 2rem;
}
#casestudies .carousel .block-text {
  height: 70%;
  padding-top: 0;
  max-width: none;
  width: auto;
}

@media (min-width: 1280px) {
  #casestudies .carousel .block-text {
    padding-right: 0;
  }
}
#casestudies .carousel .block-text > div {
  justify-content: space-between;
}
#casestudies .carousel .block-links {
  padding-top: 0;
}
#casestudies .block-header {
  margin-left: 3rem;
  margin-right: 3rem;
}
#casestudies .block-desc {
  font-weight: 250;
}

#savecollect .grid h1 {
  font-size: 50px;
}
#savecollect .grid p {
  line-height: 50px;
  font-size: 24px;
  font-weight: 300;
}
#savecollect > div > .grid > div > div {
  margin-left: 6rem;
  margin-right: 6rem;
}
#savecollect .grid + .grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 6rem;
  margin-left: 6rem;
  margin-right: 6rem;
}
#savecollect .grid + .grid h1 {
  font-size: 25px;
}
#savecollect .grid + .grid h3 {
  line-height: 40px;
  font-size: 20px;
}
#savecollect .grid + .grid > div > div {
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  padding-top: 2rem;
  padding-bottom: 3rem;
}
#savecollect .grid + .grid > div > div > div > div > div {
  justify-content: flex-start;
}
#savecollect .grid + .grid > div:nth-child(1) > div {
  background-color: #fed0468a;
}
#savecollect .grid + .grid > div:nth-child(2) > div {
  background-color: #dc8bd78a;
}
#savecollect .grid + .grid > div:nth-child(3) > div {
  background-color: #ff6a358a;
}

@media (max-width: 1024px) {
  #savecollect .grid + .grid {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

#tools p {
  font-weight: 300;
  line-height: 50px;
}

#stories {
  margin-top: 3rem;
  margin-bottom: 3rem;
}
#stories p {
  font-family: var(--font-alexanderia);
}
#stories .grid > div {
  max-width: 1512px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  padding-left: 3rem;
  padding-right: 3rem;
}
#stories .carousel {
  margin-top: 1rem;
}
#stories .carousel p {
  font-size: 1.25rem;
  line-height: 1.5;
  word-spacing: 2%;
  padding: 8px 4px;
  font-weight: 400;
}
#stories .carousel p,
#stories .carousel h3 {
  color: #042e2c;
}
#stories .carousel div:has(> h3) {
  background-color: transparent;
  border-top: 1px solid #98989836;
}
#stories .carousel__slide.is-odd .block-media-wrapper img {
  aspect-ratio: 5 / 4;
}
#stories .carousel__slide.is-even .block-media-wrapper img {
  aspect-ratio: 16 / 9;
}
#stories .carousel .block-media-wrapper img {
  object-fit: cover;
  object-position: 0 25%;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

#team-content {
  margin-right: auto;
  margin-left: auto;
  max-width: 900px;
}
#team-content > div {
  width: 100%;
}

[id^='case-'][id$='-student'] {
  & .block-header {
    width: 100%;
    max-width: 70rem;
    padding-left: 6rem;
    padding-right: 6rem;
    margin-left: auto;
    margin-right: auto;
  }
  & .carousel__slide > div {
    height: auto;
    border-radius: 8px 8px 0 0;
    border-bottom: 8px solid #a764a4;
  }
  & .carousel__slide .block-media-bg > div {
    display: block;
    margin-bottom: -2px;
  }
  & .carousel__slide .block-media-text {
    display: none;
  }
}

@media (max-width: 1024px) {
  [id^='case-'][id$='-student'] .block-header {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

@media (max-width: 640px) {
  #casestudies .block-text:has(.block-header) {
    padding: 0;
  }
  #casestudies .block-media {
    display: none;
  }
  #casestudies .block-desc p {
    font-size: 1.2rem;
  }
  #casestudies .carousel .block-text {
    margin: 1.5rem;
  }
  #casestudies .carousel__slide > div {
    height: 18rem;
  }
  #casestudies .carousel__slide > div > div {
    padding: 0;
  }
  #savecollect .grid h1 {
    font-size: 2rem;
  }
  #savecollect .grid p {
    line-height: 1.5;
    width: 100%;
  }
  #savecollect > div > .grid > div > div {
    margin-left: 3rem;
    margin-right: 3rem;
  }
  #savecollect .grid + .grid {
    margin-bottom: 3rem;
    margin-left: 3rem;
    margin-right: 3rem;
  }
  #savecollect .grid + .grid h1 {
    font-size: 1.4rem;
    margin-top: 0;
  }
  #savecollect .grid + .grid img {
    width: 36px;
  }
  #tools > div {
    padding-left: 0;
    padding-right: 0;
  }
  #tools p {
    font-size: 1.3rem;
    line-height: 1.5;
    font-weight: 200;
  }
  #tools > div > div:first-child :has(> h1) {
    margin-top: 0;
  }
  #tools > div > div:first-child :has(> h1) h1 {
    font-size: 2rem;
  }
  #tools > div > div:first-child > div {
    margin-top: 3rem;
    margin-bottom: 3rem;
  }
  #stories > div > div > div {
    margin-top: 0;
    margin-bottom: 1.5rem;
  }
  #stories > div > div > div > div {
    margin: 0;
  }
  #stories .carousel p {
    font-size: 1.1rem;
  }
  #stories .carousel h3 {
    font-size: 1.2rem;
  }
}
</style>
